import { Badge, Button } from "@/components/ui";
import { Settings2 } from "lucide-react";
import { SubscriptionServices, VideoProjectServices } from "@/services";
import { toast } from "sonner";
import * as React from "react";
import { useEffect } from "react";
import {
  PricePlanTier,
  SubscribedPlayerSearchResponse,
  VideoCatalystStatus,
  VideoProjectCoachConfig,
  WebSettings,
} from "@/interfaces";
import { printOnlyDate, useAccountSettings, useSocialCoachSession } from "@/utils";
import { VideoCreditsModal } from "@/containers/Coach/EditPlayer/components/VideoCreditsModal";
import { FormCard } from "@/components/FormCard";

interface Props {
  player: SubscribedPlayerSearchResponse;
  withWritePermission: boolean;
  clearCaches: () => void;
  reloadPlayer: () => void;
}

const getOrdinalSuffix = (i: number) => {
  const j = i % 10,
    k = i % 100;
  if (j === 1 && k !== 11) return i + "st";
  if (j === 2 && k !== 12) return i + "nd";
  if (j === 3 && k !== 13) return i + "rd";
  return i + "th";
};

export function PlayerSubscriptionForm({ player, withWritePermission, clearCaches, reloadPlayer }: Props) {
  const [changingPlans, setChangingPlans] = React.useState<boolean>(false);
  const [changingStatus, setChangingStatus] = React.useState<boolean>(false);
  const [showCreditsModal, setShowCreditsModal] = React.useState(false);
  const [vcStatus, setVCStatus] = React.useState<VideoCatalystStatus>();
  const [coachVCConfig, setCoachVCConfig] = React.useState<VideoProjectCoachConfig>();

  const playerAccount = player.playerAccount;
  const playerId = playerAccount.playerId!;
  const { coachId } = useSocialCoachSession();

  const subscriptionType = player.pricePlanSubscription?.pricePlan.tier;
  const verificationDate = player.playerAccount.verificationDate;
  const isActivePlayer = player.pricePlanSubscription!.status !== "CANCELED";

  const { hasAccountSetting } = useAccountSettings();
  const scPlusFeatureActive = hasAccountSetting(WebSettings.SC_PLUS_TIER);

  const reactivateSubscription = () => {
    if (window.confirm("Are you sure you want to reactivate this Player?")) {
      setChangingStatus(true);
      SubscriptionServices.reactivateSubscription(playerId, coachId!)
        .then(() => {
          clearCaches();
          toast.success("This player has been reactivated. They will receive an email shortly.");
          reloadPlayer();
        })
        .catch(e => toast.error(`Error reactivating player: ${e}`))
        .finally(() => setChangingStatus(false));
    }
  };

  const cancelSubscription = () => {
    if (window.confirm("Are you sure you want to cancel this Player's subscription?")) {
      setChangingStatus(true);
      SubscriptionServices.cancelSubscription(playerId, coachId!)
        .then(() => {
          clearCaches();
          toast.info("Player’s subscription has been canceled.");
          reloadPlayer();
        })
        .catch(e => toast.error(`Error cancelling player: ${e}`))
        .finally(() => setChangingStatus(false));
    }
  };

  const updateSubscriptionType = (type: PricePlanTier): void => {
    if (!playerId) return;
    setChangingPlans(true);
    SubscriptionServices.updatePricePlanTier(playerId, coachId!, type)
      .then(() => {
        clearCaches();
        reloadPlayer();
      })
      .catch((e: string) => toast.error(e.substring(0, 40)))
      .finally(() => setChangingPlans(false));
  };

  const updateSubscriptionTypeWithValidation = (type: PricePlanTier): void => {
    if (type === PricePlanTier.PLAYER_BASIC) {
      if (window.confirm("Are you sure you want to downgrade this Player to SocialCoach Basic?")) {
        updateSubscriptionType(type);
      }
    } else {
      updateSubscriptionType(type);
    }
  };

  const getPlanName = () => {
    if (!isActivePlayer) return "Inactive Plan";
    if (!scPlusFeatureActive) return "SocialCoach Basic";
    if (subscriptionType === undefined) return "Unknown Tier";

    switch (subscriptionType) {
      case PricePlanTier.PLAYER_BASIC:
        return "SocialCoach Basic";
      case PricePlanTier.PLAYER_PLUS:
        return "SocialCoach+";
      default:
        return "SocialCoach Basic";
    }
  };

  useEffect(() => {
    if (coachId) {
      VideoProjectServices.getCoachConfig(coachId).then(setCoachVCConfig).catch(console.error);
    }
  }, [coachId]);

  useEffect(() => {
    if (playerId) {
      VideoProjectServices.getVideoCatalystStatus(playerId).then(setVCStatus).catch(console.error);
    }
  }, [playerId]);

  return (
    <>
      <FormCard>
        <FormCard.Header title="Subscription" description="Plan details and billing status" />
        <FormCard.Content>
          {/* Increased padding/gap for better vertical spacing */}
          <div className="flex flex-col gap-6 pt-4">
            {/* HERO SECTION: Plan, Status, and Date */}
            <div className="flex flex-col gap-1.5">
              <div className="flex justify-between items-center">
                <div className="text-2xl font-bold tracking-tight text-foreground">{getPlanName()}</div>
                <Badge variant={isActivePlayer ? "default" : "destructive"}>
                  {isActivePlayer ? "Active" : "Inactive"}
                </Badge>
              </div>
              {verificationDate && (
                <div className="text-sm text-muted-foreground">Subscribed on {printOnlyDate(verificationDate)}</div>
              )}
            </div>

            {/* HIGHLIGHT SECTION: Video Credits */}
            {vcStatus && subscriptionType === PricePlanTier.PLAYER_PLUS && (
              <div className="flex flex-col gap-3 bg-slate-50 rounded-2xl border border-slate-100 p-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-foreground">Video Credits</span>
                  {withWritePermission && isActivePlayer && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 gap-1.5"
                      onClick={() => setShowCreditsModal(true)}
                    >
                      <Settings2 className="h-3.5 w-3.5" />
                      Manage
                    </Button>
                  )}
                </div>

                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold tracking-tight">{vcStatus.creditsUsed}</span>
                  <span className="text-sm font-medium text-muted-foreground">
                    / {vcStatus.unlimitedCredits ? "Unlimited" : vcStatus.creditsAllotted} used
                  </span>
                </div>

                {/* New Cycle Day Context */}
                {coachVCConfig?.newCycleDay && !vcStatus.unlimitedCredits && (
                  <p className="text-xs text-muted-foreground">
                    Credits reset on the {getOrdinalSuffix(coachVCConfig.newCycleDay)} of every month.
                  </p>
                )}
              </div>
            )}
          </div>
        </FormCard.Content>

        {withWritePermission && (
          <FormCard.Actions>
            {isActivePlayer ? (
              <Button
                variant="ghost"
                className="text-destructive"
                onClick={cancelSubscription}
                disabled={changingStatus}
              >
                Deactivate
              </Button>
            ) : (
              <Button
                variant="default"
                onClick={reactivateSubscription}
                disabled={changingStatus || changingPlans}
                loading={changingStatus}
              >
                Re-activate
              </Button>
            )}

            {isActivePlayer &&
              scPlusFeatureActive &&
              (subscriptionType === PricePlanTier.PLAYER_BASIC ? (
                <Button
                  variant="default"
                  onClick={() => updateSubscriptionTypeWithValidation(PricePlanTier.PLAYER_PLUS)}
                  disabled={changingStatus || changingPlans}
                  loading={changingPlans}
                >
                  Upgrade to Plus
                </Button>
              ) : (
                <Button
                  variant="destructive"
                  onClick={() => updateSubscriptionTypeWithValidation(PricePlanTier.PLAYER_BASIC)}
                  disabled={changingStatus || changingPlans}
                  loading={changingPlans}
                >
                  Downgrade to Basic
                </Button>
              ))}
          </FormCard.Actions>
        )}
      </FormCard>

      <VideoCreditsModal
        playerId={playerAccount.playerId}
        vcStatus={vcStatus}
        coachVCConfig={coachVCConfig}
        open={showCreditsModal}
        onUpdateStatus={setVCStatus}
        onClose={() => setShowCreditsModal(false)}
      />
    </>
  );
}
